import { useLazyQuery } from '@apollo/client'
import { useState } from 'react'

import { EVALUATE_PROMOTION_QUERY } from '../../../../graphql/promotion'
import { buildPromotionIntent } from '../../../../helpers/promotion'
import {
  EvaluatedPromotion,
  PromotionEvaluation,
  SkippedPromotion
} from '../../../../types/promotion'
import { selectRefusal } from './refusal'

type Verdict = {
  /** The promotion the entered code applied, if it did. */
  appliedForCode?: EvaluatedPromotion
  /** The current cart-wide result, including automatic savings. */
  evaluation?: PromotionEvaluation
  /** Why it did not, if it did not. */
  refusal?: SkippedPromotion
  /** The check itself failed, and the failure belongs to the code in the field now. */
  failed: boolean
  /** The code was refused locally, before any request — it is longer than the maximum. */
  tooLong: boolean
  checking: boolean
  check: (rawCode: string) => void
}

/**
 * Checking a code against a cart, and keeping the answer attached to the code that
 * produced it.
 *
 * The fencing is why this is a hook rather than a few lines in the component. A verdict
 * belongs to the string it was computed for, so `checkedCode` — the code actually SENT —
 * is tracked separately from what is in the field now, and every part of the verdict is
 * gated on the two matching. Leaving a verdict beside an edited code credits a passed
 * check to something nobody checked.
 *
 * Derived rather than cleared in an effect: an effect would be a second source of truth
 * for the same fact, and the two drift.
 *
 * `!checking` is load-bearing here. `checkedCode` is set synchronously on Apply while the
 * query keeps the PREVIOUS response until the new one lands, so on a second Apply with a
 * different code there is a window where `checkedCode` already equals the new code and the
 * data still describes the old one. Pinning that window needs a delayed resolver
 * (BL-063).
 *
 * The length check runs through `buildPromotionIntent`, the same guard the booking path
 * uses. It was previously absent here: Apply built its variables inline and relied on the
 * field's maxLength attribute, which is an input-editing convenience rather than an
 * application control — so the one function that exists to bound this was the one path not
 * using it.
 */
export function usePromotionEvaluation(
  cartID: string,
  code: string,
  cartKey = cartID
): Verdict {
  const [checkedCode, setCheckedCode] = useState<string>()
  const [lastSeenCode, setLastSeenCode] = useState<string>(code.trim())
  const [lastSeenCartKey, setLastSeenCartKey] = useState(cartKey)
  const [tooLong, setTooLong] = useState(false)
  const [evaluate, { data, loading: checking, error }] = useLazyQuery<{
    evaluatePromotion: PromotionEvaluation
  }>(EVALUATE_PROMOTION_QUERY, { fetchPolicy: 'network-only' })

  const pending = code.trim()

  if (cartKey !== lastSeenCartKey) {
    setLastSeenCartKey(cartKey)
    setCheckedCode(undefined)
  }

  // Invalidate the moment the code moves AWAY from what was checked, not merely while it
  // differs. `isCurrent` is string equality and `checkedCode` was never reset, so a code
  // that was applied, cleared, and then retyped re-satisfied the comparison and re-showed
  // the LAST response with no new request made — a verdict, and a discount, computed
  // against the cart as it stood before the clear.
  //
  // Reachable here through the offer list, not just by hand: re-picking the selected offer
  // clears the code (index.tsx), and picking it again restores the same string.
  //
  // Adjusted during render rather than in an effect, which is what the note above already
  // argues for — an effect is a second source of truth for the same fact and the two
  // drift. React re-runs this render with the updated state before committing.
  if (pending !== lastSeenCode) {
    setLastSeenCode(pending)
    if (pending !== checkedCode) {
      setCheckedCode(undefined)
    }
  }

  const isCurrent =
    checkedCode !== undefined && checkedCode === pending && !checking

  const result = isCurrent ? data?.evaluatePromotion : undefined
  const appliedForCode = result?.applied.find(
    (promotion) => promotion.distribution === 'CodeEntry'
  )

  return {
    appliedForCode,
    evaluation: result,
    refusal: selectRefusal(result, appliedForCode !== undefined),
    failed: Boolean(error) && isCurrent,
    tooLong,
    checking,
    check: (rawCode: string) => {
      const trimmed = rawCode.trim()
      if (trimmed === '') return

      const intent = buildPromotionIntent(rawCode)
      if (!intent) {
        setTooLong(true)
        return
      }

      setTooLong(false)
      setCheckedCode(trimmed)
      evaluate({ variables: { cartID, intent } })
    }
  }
}
